import axios from 'axios';
import { EventData, PredictionData, FightPrediction } from '../types';
import { format } from 'date-fns';

// Base URL for API - environment specific
export const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://fight-vision-backend-production.up.railway.app';

// Scraper API base URL - add server URL
const SCRAPER_API_URL = process.env.REACT_APP_SCRAPER_API_URL || API_BASE_URL;

// Open Fight Vision API base URL
export const OFV_API_URL = process.env.REACT_APP_OFV_API_URL || API_BASE_URL;

// Prediction tracker endpoint - use the API endpoint
const PREDICTION_TRACKER_URL = `${API_BASE_URL}/api/prediction-tracking`;

// Helper function to get formatted date string for a specific date
const getFormattedDate = (date: Date): string => {
  return format(date, 'yyyyMMdd');
};

// Clear axios request headers to prevent 431 errors
export const clearRequestHeaders = () => {
  // Remove any custom headers that might be causing issues
  delete axios.defaults.headers.common['X-Custom-Header'];
  
  // Ensure Accept header is simple
  axios.defaults.headers.common['Accept'] = 'application/json';
  
  // Create a minimal axios instance if needed
  return axios.create({
    headers: {
      'Accept': 'application/json',
      'Content-Type': 'application/json'
    }
  });
};

// Check API health
export const checkApiHealth = async (): Promise<boolean> => {
  try {
    console.log(`Attempting API health check at: ${API_BASE_URL}/api/health`);
    const client = clearRequestHeaders();
    const url = `${API_BASE_URL}/api/health`;
    console.log(`Using client with headers:`, client.defaults.headers);
    
    const response = await client.get(url);
    console.log(`Health check response:`, response.data);
    return response.data?.status === 'ok';
  } catch (error) {
    console.error('API health check failed:', error);
    if (error instanceof Error) {
      console.error('Error details:', {
        message: error.message,
        name: error.name,
        stack: error.stack
      });
    }
    return false;
  }
};

// Fetch all fight predictions from API
export const fetchAllFightPredictions = async (): Promise<any> => {
  try {
    const client = clearRequestHeaders();
    const response = await client.get(`${API_BASE_URL}/api/predictions`);
    return response.data;
  } catch (error) {
    console.error('Error fetching fight predictions:', error);
    throw error;
  }
};

// Get all available fight events from localStorage
export const getAvailableEvents = (): string[] => {
  try {
    const storedIndex = localStorage.getItem('fight-vision-events-index');
    if (storedIndex) {
      return JSON.parse(storedIndex);
    }
  } catch (e) {
    console.warn('Error loading events index');
  }
  return [];
};

// Get prediction data for a specific event
export const getPredictionDataForEvent = (eventName: string): any => {
  try {
    const eventKey = `fight-vision-event-${eventName.replace(/\s+/g, '-').toLowerCase()}`;
    const storedData = localStorage.getItem(eventKey);
    if (storedData) {
      return JSON.parse(storedData);
    }
  } catch (e) {
    console.warn(`Error loading prediction data for event: ${eventName}`);
  }
  return null;
};

// Fetch specific fight details by ID
export const fetchFightById = async (fightId: number): Promise<any> => {
  try {
    const client = clearRequestHeaders();
    const url = `${API_BASE_URL}/api/fights/${fightId}`;
    console.log(`Fetching fight details from: ${url}`);
    
    const response = await client.get(url);
    return response.data;
  } catch (error) {
    console.error(`Error fetching fight #${fightId}:`, error);
    throw new Error(`Failed to fetch fight #${fightId}. Please try again later.`);
  }
};

// Refresh predictions by triggering the API
export const refreshPredictions = async (): Promise<any> => {
  try {
    const client = clearRequestHeaders();
    // Use full refresh to regenerate predictions with latest narrative logic
    const fullUrl = `${API_BASE_URL}/api/predictions/refresh`;
    console.log(`Refreshing predictions via: ${fullUrl}`);
    
    try {
      const response = await client.post(fullUrl);
      console.log('Full refresh successful');
      return response.data;
    } catch (fullError) {
      console.warn('Full refresh failed, trying simple refresh as fallback:', fullError);
      
      // Fallback to simple refresh (just reload existing data)
      const simpleUrl = `${API_BASE_URL}/api/predictions/refresh-simple`;
      console.log(`Falling back to simple refresh via: ${simpleUrl}`);
      const response = await client.post(simpleUrl);
      return response.data;
    }
  } catch (error) {
    console.error('Error refreshing predictions:', error);
    throw new Error('Failed to refresh predictions. Please try again later.');
  }
};

// Trigger the scraper to refresh data
export const triggerScraperRefresh = async (): Promise<{ status: string; message: string }> => {
  try {
    const client = clearRequestHeaders();
    const url = `${API_BASE_URL}/api/predictions/refresh`;
    console.log(`Triggering scraper refresh at: ${url}`);
    
    const response = await client.post(url);
    return response.data;
  } catch (error) {
    console.error('Error triggering scraper refresh:', error);
    throw new Error('Failed to trigger scraper refresh. Please try again later.');
  }
};

// Fetch the latest integrated fight data
export const fetchIntegratedFightData = async (): Promise<EventData> => {
  // Only try to get data from the API
  const client = clearRequestHeaders();
  const apiUrl = `${API_BASE_URL}/api/predictions/raw`;
  console.log(`Fetching integrated data from API: ${apiUrl}`);
  
  const response = await client.get<EventData>(apiUrl);
  return response.data;
};

// Fetch the latest prediction input data
export const fetchPredictionInputData = async (): Promise<PredictionData> => {
  // Only try to get data from the API
  const client = clearRequestHeaders();
  const apiUrl = `${API_BASE_URL}/api/predictions/raw`;
  console.log(`Fetching prediction data from API: ${apiUrl}`);
  
  const response = await client.get<PredictionData>(apiUrl);
  return response.data;
};

// Simple sigmoid function to convert model differentials to probability
const sigmoid = (x: number): number => {
  return 1 / (1 + Math.exp(-x));
};

// Calculate win probability based on model differentials
export const calculateWinProbability = (modelDiffs: any): number => {
  // Create a weighted sum of the differentials
  // These weights should be tuned based on model performance
  const weightedSum = 
    modelDiffs.SLPM_delta * 0.25 + 
    modelDiffs.SAPM_delta * 0.2 + 
    modelDiffs.STRD_delta * 0.15 + 
    modelDiffs.TD_delta * 0.15 -
    modelDiffs.odds_delta * 0.25;
  
  // Convert to probability using sigmoid
  return sigmoid(weightedSum);
};

// Generate explanation for prediction
export const generateExplanation = (prediction: any): string => {
  const { fighter1_name, fighter2_name, model_diffs, fighter1_odds, fighter2_odds } = prediction;
  
  // Calculate win probabilities
  const fighter1WinProb = calculateWinProbability(model_diffs);
  const fighter2WinProb = 1 - fighter1WinProb;
  
  // Determine predicted winner and underdog
  const predictedWinner = fighter1WinProb > 0.5 ? fighter1_name : fighter2_name;
  const predictedLoser = fighter1WinProb > 0.5 ? fighter2_name : fighter1_name;
  const winnerProb = Math.round((fighter1WinProb > 0.5 ? fighter1WinProb : fighter2WinProb) * 100);
  const loserProb = 100 - winnerProb;
  
  // Extract last names
  const winnerLastName = predictedWinner.split(' ').pop();
  const loserLastName = predictedLoser.split(' ').pop();
  
  // Get the betting favorite
  const bettingFavorite = fighter1_odds < fighter2_odds ? fighter1_name : fighter2_name;
  const bettingFavoriteLastName = bettingFavorite.split(' ').pop();
  const bettingAlignedWithPrediction = predictedWinner === bettingFavorite;
  
  // Calculate the betting implied probability
  const bettingFavoriteOdds = fighter1_odds < fighter2_odds ? fighter1_odds : fighter2_odds;
  const bettingUnderdogOdds = fighter1_odds < fighter2_odds ? fighter2_odds : fighter1_odds;
  
  // Convert American odds to implied probability
  const getImpliedProbability = (americanOdds: number): number => {
    if (americanOdds > 0) {
      return 100 / (americanOdds + 100);
    } else {
      return -americanOdds / (-americanOdds + 100);
    }
  };
  
  const bettingFavoriteImpliedProb = Math.round(getImpliedProbability(bettingFavoriteOdds) * 100);
  
  // Define the fighting categories with key differentials and interpretations
  const fightCategories = [
    {
      name: 'striking',
      stats: [
        { 
          key: 'STRD_delta', 
          value: model_diffs.STRD_delta,
          winnerPhrase: 'striking accuracy',
          loserPhrase: 'striking precision',
          narrativeWinnerPhrase: 'lands with more precision',
          narrativeLoserPhrase: 'connects with better accuracy'
        },
        { 
          key: 'SLPM_delta', 
          value: model_diffs.SLPM_delta,
          winnerPhrase: 'striking volume',
          loserPhrase: 'striking output',
          narrativeWinnerPhrase: 'throws more strikes per minute',
          narrativeLoserPhrase: 'maintains a higher striking pace'
        }
      ]
    },
    {
      name: 'defense',
      stats: [
        { 
          key: 'SAPM_delta', 
          value: -model_diffs.SAPM_delta, // Reversed because lower is better
          winnerPhrase: 'striking defense',
          loserPhrase: 'defensive movement',
          narrativeWinnerPhrase: 'absorbs fewer strikes',
          narrativeLoserPhrase: 'shows better defensive awareness'
        }
      ]
    },
    {
      name: 'grappling',
      stats: [
        { 
          key: 'TD_delta', 
          value: model_diffs.TD_delta,
          winnerPhrase: 'takedown offense',
          loserPhrase: 'takedown ability',
          narrativeWinnerPhrase: 'secures more takedowns',
          narrativeLoserPhrase: 'demonstrates better wrestling'
        },
        { 
          key: 'TDD_delta', 
          value: model_diffs.TDD_delta || 0,
          winnerPhrase: 'takedown defense',
          loserPhrase: 'takedown resistance',
          narrativeWinnerPhrase: 'defends takedowns more effectively',
          narrativeLoserPhrase: 'shows stronger takedown defense'
        }
      ]
    }
  ];
  
  // Find the winner's key advantages across categories
  const winnerStrengths: {phrase: string, narrativePhrase: string, value: number, category: string}[] = [];
  
  // First, determine if fighter1 or fighter2 is the winner
  const isWinnerFighter1 = fighter1WinProb > 0.5;
  
  // For each category, find the winner's major advantages
  fightCategories.forEach(category => {
    // Find the most significant advantage in this category
    const biggestAdvantage = category.stats
      .filter(stat => {
        // For fighter 1 as winner, positive values are advantages
        // For fighter 2 as winner, negative values are advantages
        return isWinnerFighter1 ? stat.value > 0 : stat.value < 0;
      })
      .sort((a, b) => Math.abs(b.value) - Math.abs(a.value))[0];
    
    // Use a threshold to avoid over-characterization
    if (biggestAdvantage && Math.abs(biggestAdvantage.value) > (category.name === 'grappling' ? 0.4 : 0.3)) {
      winnerStrengths.push({
        phrase: biggestAdvantage.winnerPhrase, 
        narrativePhrase: biggestAdvantage.narrativeWinnerPhrase,
        value: Math.abs(biggestAdvantage.value),
        category: category.name
      });
    }
  });
  
  // Find the loser's actual strengths (areas where they might have an edge)
  const loserStrengths: {phrase: string, narrativePhrase: string, value: number, category: string}[] = [];
  
  // For each category, find if the loser has any advantages
  fightCategories.forEach(category => {
    // Find if the loser has any advantage in this category
    const loserAdvantage = category.stats
      .filter(stat => {
        // For fighter 1 as loser, positive values would be advantages
        // For fighter 2 as loser, negative values would be advantages
        return !isWinnerFighter1 ? stat.value > 0 : stat.value < 0;
      })
      .sort((a, b) => Math.abs(b.value) - Math.abs(a.value))[0];
    
    // Use a threshold for loser advantages too
    if (loserAdvantage && Math.abs(loserAdvantage.value) > (category.name === 'grappling' ? 0.35 : 0.25)) {
      loserStrengths.push({
        phrase: loserAdvantage.loserPhrase,
        narrativePhrase: loserAdvantage.narrativeLoserPhrase,
        value: Math.abs(loserAdvantage.value),
        category: category.name
      });
    }
  });
  
  // Sort strengths by value (most significant first)
  winnerStrengths.sort((a, b) => b.value - a.value);
  loserStrengths.sort((a, b) => b.value - a.value);
  
  // Generate the explanation
  let explanation = '';
  
  // SECTION 1: INTRODUCTION WITH PREDICTION AND CONFIDENCE
  const confidenceLevel = winnerProb >= 65 ? "strongly" : winnerProb >= 55 ? "moderately" : "slightly";
  
  const introTemplates = [
    `Our analysis ${confidenceLevel} favors ${predictedWinner} (${winnerProb}%) over ${predictedLoser} in this matchup.`,
    `The data points to a ${winnerProb}% edge for ${predictedWinner} against ${predictedLoser}.`, 
    `${predictedWinner} emerges as the statistical favorite with a ${winnerProb}% win probability.`,
    `At ${winnerProb}%, ${predictedWinner} holds a ${confidenceLevel} favorable position against ${predictedLoser}.`
  ];
  
  explanation = introTemplates[Math.floor(Math.random() * introTemplates.length)] + ' ';
  
  // SECTION 2: NARRATIVE BREAKDOWN OF THE MATCHUP
  let narrativeSection = '';
  
  // If we have at least one winner strength and one loser strength
  if (winnerStrengths.length > 0 && loserStrengths.length > 0) {
    const winnerAdvantage = winnerStrengths[0];
    const loserAdvantage = loserStrengths[0];
    
    const narrativeTemplates = [
      `This fight presents a classic styles clash: ${winnerLastName} ${winnerAdvantage.narrativePhrase}, while ${loserLastName} ${loserAdvantage.narrativePhrase}.`,
      `The numbers tell a compelling story: ${winnerLastName}'s edge in ${winnerAdvantage.phrase} against ${loserLastName}'s advantage in ${loserAdvantage.phrase}.`,
      `Looking at the matchup dynamics, ${winnerLastName} ${winnerAdvantage.narrativePhrase}, but ${loserLastName} counters by ${loserAdvantage.narrativePhrase.split(' ').slice(1).join(' ')}.`
    ];
    
    narrativeSection = narrativeTemplates[Math.floor(Math.random() * narrativeTemplates.length)] + ' ';
  }
  // If we have multiple winner strengths but no real loser advantages
  else if (winnerStrengths.length >= 2 && loserStrengths.length === 0) {
    const winnerAdvantage1 = winnerStrengths[0];
    const winnerAdvantage2 = winnerStrengths[1];
    
    const dominanceTemplates = [
      `${winnerLastName} has significant statistical advantages, particularly in ${winnerAdvantage1.phrase} and ${winnerAdvantage2.phrase}.`,
      `The metrics favor ${winnerLastName} across multiple dimensions, especially ${winnerAdvantage1.phrase} and ${winnerAdvantage2.phrase}.`,
      `${winnerLastName} demonstrates clear edges in key areas: ${winnerAdvantage1.narrativePhrase} and ${winnerAdvantage2.narrativePhrase}.`
    ];
    
    narrativeSection = dominanceTemplates[Math.floor(Math.random() * dominanceTemplates.length)] + ' ';
  }
  // If we have just one winner strength or no real advantages
  else {
    if (winnerProb >= 60) {
      const templates = [
        `The statistical edge for ${winnerLastName} is subtle but consistent across multiple performance metrics.`,
        `${winnerLastName}'s overall statistical profile shows advantages in several areas, even if no single metric stands out dramatically.`,
        `While no individual stat tells the whole story, ${winnerLastName}'s cumulative advantages create a compelling case.`
    ];
    
      narrativeSection = templates[Math.floor(Math.random() * templates.length)] + ' ';
  } else {
      const templates = [
        `This appears to be an extremely close matchup with razor-thin margins in most statistical categories.`,
        `The numbers suggest this fight could be very competitive, with neither fighter holding overwhelming advantages.`,
        `Statistically, this is one of the more evenly matched fights on the card, with minimal separation in key metrics.`
    ];
    
      narrativeSection = templates[Math.floor(Math.random() * templates.length)] + ' ';
  }
  }
  
  explanation += narrativeSection;
  
  // SECTION 3: POTENTIAL FIGHT STRATEGIES AND PATHS TO VICTORY
  let strategySection = '';
  
  // If the winner has striking advantages and the loser has grappling advantages
  if (winnerStrengths.some(s => s.category === 'striking') && loserStrengths.some(s => s.category === 'grappling')) {
    const templates = [
      `For ${winnerLastName}, keeping this fight at range and utilizing striking advantages will be critical, while ${loserLastName}'s best path runs through grappling exchanges.`,
      `${winnerLastName} should look to keep this standing where the numbers favor him, whereas ${loserLastName} likely needs to focus on closing distance and initiating grappling.`,
      `The data suggests ${winnerLastName} thrives in striking scenarios, while ${loserLastName} needs to leverage his grappling to change the fight's trajectory.`
    ];
    
    strategySection = templates[Math.floor(Math.random() * templates.length)] + ' ';
  }
  // If the winner has grappling advantages and the loser has striking advantages
  else if (winnerStrengths.some(s => s.category === 'grappling') && loserStrengths.some(s => s.category === 'striking')) {
    const templates = [
      `${winnerLastName}'s path to victory appears clearest through wrestling and control, while ${loserLastName} needs to maintain distance and leverage striking advantages.`,
      `The stats suggest ${winnerLastName} should pursue a grappling-heavy approach, whereas ${loserLastName}'s striking could be his equalizer if kept standing.`,
      `Look for ${winnerLastName} to initiate grappling exchanges where he has the advantage, while ${loserLastName} will aim to keep separation and capitalize on striking opportunities.`
    ];
    
    strategySection = templates[Math.floor(Math.random() * templates.length)] + ' ';
  }
  // If both have similar types of advantages or no clear pattern
  else if (winnerStrengths.length > 0 || loserStrengths.length > 0) {
    const winnerArea = winnerStrengths.length > 0 ? winnerStrengths[0].category : "overall game";
    const loserArea = loserStrengths.length > 0 ? loserStrengths[0].category : "determination";
    
    const templates = [
      `${winnerLastName}'s path to victory runs through maximizing his ${winnerArea} advantages, while ${loserLastName} will need to leverage his ${loserArea} to stay competitive.`,
      `For ${winnerLastName}, success likely depends on exploiting his edge in ${winnerArea}, but ${loserLastName}'s ${loserArea} could make this more competitive than expected.`,
      `${winnerLastName} should look to dictate the fight in ${winnerArea} exchanges, though ${loserLastName}'s ${loserArea} gives him a puncher's chance.`
    ];
    
    strategySection = templates[Math.floor(Math.random() * templates.length)] + ' ';
  }
  
  explanation += strategySection;
  
  // SECTION 4: BETTING VALUE ASSESSMENT
  let bettingSection = '';
  
  if (bettingAlignedWithPrediction) {
    // Model agrees with the betting markets
    const modelVsMarketDiff = Math.abs(winnerProb - bettingFavoriteImpliedProb);
    
    if (modelVsMarketDiff >= 7) {
      // Significant edge detected
      const templates = [
        `There appears to be betting value on ${winnerLastName}, as our ${winnerProb}% projection exceeds the market's implied ${bettingFavoriteImpliedProb}% probability.`,
        `At current odds, ${winnerLastName} presents potential value with our model giving him a ${winnerProb}% chance compared to the betting market's ${bettingFavoriteImpliedProb}%.`,
        `Bettors may find an edge backing ${winnerLastName}, with our model showing a ${modelVsMarketDiff}% advantage over the betting line.`
      ];
      
      bettingSection = templates[Math.floor(Math.random() * templates.length)];
    } else {
      // No significant edge
      const templates = [
        `The betting market assessment (${bettingFavoriteImpliedProb}%) aligns closely with our model's projection (${winnerProb}%).`,
        `Our model and the betting markets are in general agreement on this fight's probabilities.`,
        `Current odds fairly reflect the statistical outlook for this matchup.`
      ];
      
      bettingSection = templates[Math.floor(Math.random() * templates.length)];
    }
  } else {
    // Model disagrees with betting markets (potential upset)
    const templates = [
      `Interestingly, our model diverges from the betting markets, which favor ${bettingFavoriteLastName} despite our statistical edge for ${winnerLastName}.`,
      `This presents a potential upset opportunity, with ${winnerLastName} as the statistical pick despite ${bettingFavoriteLastName} being favored in the betting markets.`,
      `While oddsmakers have installed ${bettingFavoriteLastName} as the favorite, our analysis suggests ${winnerLastName} has a ${winnerProb}% chance to pull the upset.`
    ];
    
    bettingSection = templates[Math.floor(Math.random() * templates.length)];
  }
  
  explanation += bettingSection;
  
  return explanation;
};

// Get important stats in order of importance
export const getImportantStats = (prediction: any, integrated?: any): Array<{name: string, fighter1Value: number, fighter2Value: number, unit?: string}> => {
  const { model_diffs, fighter1_odds, fighter2_odds, fighter1_name, fighter2_name } = prediction;
  
  // Find the corresponding fight in the integrated data if available
  let f1Stats = null;
  let f2Stats = null;
  
  if (integrated) {
    const matchedFight = integrated.fights.find(
      (f: any) => f.fighter1_name === fighter1_name && f.fighter2_name === fighter2_name
    );
    
    if (matchedFight) {
      f1Stats = matchedFight.fighter1_stats;
      f2Stats = matchedFight.fighter2_stats;
    }
  }
  
  // These are the key stats we want to show in order of importance (based on absolute differential)
  const statsToShow = [
    { 
      key: 'odds_delta', 
      name: 'Betting Odds', 
      fighter1Value: fighter1_odds, 
      fighter2Value: fighter2_odds 
    },
    { 
      key: 'SLPM_delta', 
      name: 'Strikes Landed Per Min', 
      fighter1Value: f1Stats ? f1Stats.SLPM : 0, 
      fighter2Value: f2Stats ? f2Stats.SLPM : 0, 
      unit: '' 
    },
    { 
      key: 'SAPM_delta', 
      name: 'Strikes Absorbed Per Min', 
      fighter1Value: f1Stats ? f1Stats.SAPM : 0, 
      fighter2Value: f2Stats ? f2Stats.SAPM : 0, 
      unit: '' 
    },
    { 
      key: 'STRD_delta', 
      name: 'Striking Accuracy', 
      fighter1Value: f1Stats ? Math.round(f1Stats.str_acc * 100) : 0, 
      fighter2Value: f2Stats ? Math.round(f2Stats.str_acc * 100) : 0, 
      unit: '%' 
    },
    { 
      key: 'TD_delta', 
      name: 'Takedowns Avg', 
      fighter1Value: f1Stats ? f1Stats.TD : 0, 
      fighter2Value: f2Stats ? f2Stats.TD : 0, 
      unit: '' 
    }
  ];
  
  // Sort by absolute differential value to determine importance
  const sortedDiffs = Object.entries(model_diffs)
    .map(([key, value]) => ({ key, absValue: Math.abs(value as number) }))
    .sort((a, b) => b.absValue - a.absValue);
  
  // Make sure the odds are always included
  const oddsStats = statsToShow.find(stat => stat.key === 'odds_delta');
  
  // Get the rest of the important stats, excluding the odds if already selected
  const otherStatsToShow = statsToShow
    .filter(stat => stat !== oddsStats)
    .sort((a, b) => {
      const aIndex = sortedDiffs.findIndex(diff => diff.key === a.key);
      const bIndex = sortedDiffs.findIndex(diff => diff.key === b.key);
      return aIndex - bIndex;
    })
    .slice(0, 3); // Get top 3 important stats (excluding odds)
  
  // Combine odds and other important stats
  const prioritizedStats = oddsStats 
    ? [oddsStats, ...otherStatsToShow] 
    : otherStatsToShow.slice(0, 4);
  
  return prioritizedStats;
};

// Define the interface for fighter records data
interface FighterRecord {
  name: string;
  wins: number;
  losses: number;
  draws: number;
  weight_class: string;
}

interface FighterRecordsData {
  event: {
    name: string;
    date: string;
    url: string;
  };
  fighters: FighterRecord[];
}

// Fetch fighter records from API
export const fetchFighterRecords = async (): Promise<FighterRecordsData | null> => {
  try {
    const client = clearRequestHeaders();
    const url = `${API_BASE_URL}/api/fighter-records`;
    console.log(`Fetching fighter records from: ${url}`);
    
    const response = await client.get<FighterRecordsData>(url);
    console.log('Fighter records response:', response.data);
    return response.data;
  } catch (error) {
    console.error('Error fetching fighter records:', error);
    return null;
  }
};

// Transform prediction input data to FightPrediction format
export const transformToPredictionView = async (predictionData: PredictionData, integratedData?: EventData): Promise<FightPrediction[]> => {
  // Attempt to fetch fighter records first
  let fighterRecordsData: FighterRecordsData | null = null;
  try {
    fighterRecordsData = await fetchFighterRecords();
    console.log('Successfully loaded fighter records:', fighterRecordsData);
  } catch (error) {
    console.warn('Failed to load fighter records, will use fallback methods:', error);
  }

  // Helper function to get a fighter's record from the records data
  const getRecordFromRecordsData = (fighterName: string): string => {
    if (!fighterRecordsData || !fighterRecordsData.fighters) {
      console.warn('No fighter records data available');
      return "TBD";
    }
    
    console.log(`Looking up record for fighter: "${fighterName}"`);
    
    // Try exact match first
    const fighter = fighterRecordsData.fighters.find(
      (f) => f.name === fighterName
    );
    
    if (fighter) {
      console.log(`Found EXACT match for ${fighterName}: ${fighter.wins}-${fighter.losses}-${fighter.draws}`);
      return `${fighter.wins}-${fighter.losses}-${fighter.draws}`;
    }
    
    // If exact match fails, try case-insensitive match
    const fighterCaseInsensitive = fighterRecordsData.fighters.find(
      (f) => f.name.toLowerCase() === fighterName.toLowerCase()
    );
    
    if (fighterCaseInsensitive) {
      console.log(`Found CASE-INSENSITIVE match for ${fighterName}: ${fighterCaseInsensitive.wins}-${fighterCaseInsensitive.losses}-${fighterCaseInsensitive.draws}`);
      return `${fighterCaseInsensitive.wins}-${fighterCaseInsensitive.losses}-${fighterCaseInsensitive.draws}`;
    }
    
    // If case-insensitive match fails, try substring match
    // This would match cases where the API might have middle names or slight name variations
    const fighterSubstring = fighterRecordsData.fighters.find(
      (f) => 
        f.name.toLowerCase().includes(fighterName.toLowerCase()) || 
        fighterName.toLowerCase().includes(f.name.toLowerCase())
    );
    
    if (fighterSubstring) {
      console.log(`Found SUBSTRING match for ${fighterName}: ${fighterSubstring.wins}-${fighterSubstring.losses}-${fighterSubstring.draws}`);
      console.log(`Matched "${fighterName}" with "${fighterSubstring.name}"`);
      return `${fighterSubstring.wins}-${fighterSubstring.losses}-${fighterSubstring.draws}`;
    }
    
    // Try tokenized matching (match on last name at minimum)
    const tokenizedName = fighterName.toLowerCase().split(' ');
    const lastName = tokenizedName[tokenizedName.length - 1];
    
    // Find fighters with matching last name
    const lastNameMatches = fighterRecordsData.fighters.filter(
      (f) => f.name.toLowerCase().includes(lastName)
    );
    
    if (lastNameMatches.length === 1) {
      // If only one fighter with this last name, it's likely the right one
      console.log(`Found LAST NAME match for ${fighterName}: ${lastNameMatches[0].wins}-${lastNameMatches[0].losses}-${lastNameMatches[0].draws}`);
      console.log(`Matched "${fighterName}" with "${lastNameMatches[0].name}" by last name`);
      return `${lastNameMatches[0].wins}-${lastNameMatches[0].losses}-${lastNameMatches[0].draws}`;
    }
    
    console.warn(`Record not found for fighter: "${fighterName}"`);
    // For debugging, log all available fighter names
    console.log('All available fighters in records:');
    fighterRecordsData.fighters.forEach((f) => {
      console.log(`- "${f.name}"`);
    });
    
    return "TBD";
  };
  
  // Check if the data follows the new API format that includes fighter records
  const isNewApiFormat = predictionData.fights.length > 0 && 
    (predictionData as any).fights[0].fighter1 && 
    (predictionData as any).fights[0].fighter2;
    
  if (isNewApiFormat) {
    // Process using the new API format (with fighter1 and fighter2 objects)
    return (predictionData as any).fights.map((fight: any) => {
      // Format fighter records
      const formatRecord = (fighter: any, fighterName: string): string => {
        // First check if we have records from the API
        const recordFromApi = getRecordFromRecordsData(fighterName);
        if (recordFromApi !== "TBD") {
          return recordFromApi;
        }
        
        // Then check if there's a direct record field
        if (fighter.record !== null && fighter.record !== undefined && fighter.record !== "null" && fighter.record !== "TBD") {
          return fighter.record;
        }
        
        // Then try to find record in integrated data if available
        if (integratedData?.fights && integratedData.fights.length > 0) {
          // Try exact name match first
          const matchedFight = integratedData.fights.find(
            (f) => f.fighter1_name === fighterName || f.fighter2_name === fighterName
          );
          
          if (matchedFight) {
            const fighterStats = matchedFight.fighter1_name === fighterName 
              ? matchedFight.fighter1_stats 
              : matchedFight.fighter2_stats;
              
            if (fighterStats && fighterStats.wins !== undefined && 
                fighterStats.losses !== undefined && 
                fighterStats.draws !== undefined) {
              console.log(`Found record for ${fighterName}: ${fighterStats.wins}-${fighterStats.losses}-${fighterStats.draws}`);
              return `${fighterStats.wins}-${fighterStats.losses}-${fighterStats.draws}`;
            }
          }
          
          // If no exact match, try case-insensitive partial match
          const partialMatch = integratedData.fights.find(
            (f) => f.fighter1_name.toLowerCase().includes(fighterName.toLowerCase()) || 
                   f.fighter2_name.toLowerCase().includes(fighterName.toLowerCase())
          );
          
          if (partialMatch) {
            const isFirstFighter = partialMatch.fighter1_name.toLowerCase().includes(fighterName.toLowerCase());
            const fighterStats = isFirstFighter ? partialMatch.fighter1_stats : partialMatch.fighter2_stats;
              
            if (fighterStats && fighterStats.wins !== undefined && 
                fighterStats.losses !== undefined && 
                fighterStats.draws !== undefined) {
              console.log(`Found partial match record for ${fighterName}: ${fighterStats.wins}-${fighterStats.losses}-${fighterStats.draws}`);
              return `${fighterStats.wins}-${fighterStats.losses}-${fighterStats.draws}`;
            }
          }
        }
        
        return "TBD";
      };
      
      return {
        fighter1: {
          name: fight.fighter1.name,
          winProbability: fight.fighter1.win_probability,
          record: formatRecord(fight.fighter1, fight.fighter1.name)
        },
        fighter2: {
          name: fight.fighter2.name,
          winProbability: fight.fighter2.win_probability,
          record: formatRecord(fight.fighter2, fight.fighter2.name)
        },
        explanation: fight.explanation,
        importantStats: fight.important_stats.map((stat: any) => ({
          name: stat.name,
          fighter1Value: stat.fighter1Value,
          fighter2Value: stat.fighter2Value,
          unit: stat.unit
        }))
      };
    });
  } else {
    // Process using the old API format
    return Promise.all(predictionData.fights.map(async fight => {
      const fighter1WinProb = calculateWinProbability(fight.model_diffs);
      const fighter2WinProb = 1 - fighter1WinProb;
      
      // Try to find records from the API first
      let fighter1Record = getRecordFromRecordsData(fight.fighter1_name);
      let fighter2Record = getRecordFromRecordsData(fight.fighter2_name);
      
      // If records are still TBD, try to find them in the integrated data
      if (fighter1Record === "TBD" || fighter2Record === "TBD") {
        if (integratedData?.fights && integratedData.fights.length > 0) {
          // Try exact match first
          const matchedFight = integratedData.fights.find(
            (f) => f.fighter1_name === fight.fighter1_name && f.fighter2_name === fight.fighter2_name ||
                  f.fighter1_name === fight.fighter2_name && f.fighter2_name === fight.fighter1_name
          );
          
          if (matchedFight) {
            // Match found - extract records
            const f1Stats = matchedFight.fighter1_name === fight.fighter1_name 
              ? matchedFight.fighter1_stats 
              : matchedFight.fighter2_stats;
              
            const f2Stats = matchedFight.fighter1_name === fight.fighter2_name 
              ? matchedFight.fighter1_stats 
              : matchedFight.fighter2_stats;
            
            if (fighter1Record === "TBD" && f1Stats && f1Stats.wins !== undefined && 
                f1Stats.losses !== undefined && f1Stats.draws !== undefined) {
              fighter1Record = `${f1Stats.wins}-${f1Stats.losses}-${f1Stats.draws}`;
            }
            
            if (fighter2Record === "TBD" && f2Stats && f2Stats.wins !== undefined && 
                f2Stats.losses !== undefined && f2Stats.draws !== undefined) {
              fighter2Record = `${f2Stats.wins}-${f2Stats.losses}-${f2Stats.draws}`;
            }
          } else {
            // Try to find individual fighter matches if full fight match not found
            const fighter1Match = integratedData.fights.find(
              (f) => f.fighter1_name === fight.fighter1_name || f.fighter2_name === fight.fighter1_name
            );
            
            const fighter2Match = integratedData.fights.find(
              (f) => f.fighter1_name === fight.fighter2_name || f.fighter2_name === fight.fighter2_name
            );
            
            if (fighter1Record === "TBD" && fighter1Match) {
              const stats = fighter1Match.fighter1_name === fight.fighter1_name 
                ? fighter1Match.fighter1_stats 
                : fighter1Match.fighter2_stats;
                
              if (stats && stats.wins !== undefined && stats.losses !== undefined && stats.draws !== undefined) {
                fighter1Record = `${stats.wins}-${stats.losses}-${stats.draws}`;
              }
            }
            
            if (fighter2Record === "TBD" && fighter2Match) {
              const stats = fighter2Match.fighter1_name === fight.fighter2_name 
                ? fighter2Match.fighter1_stats 
                : fighter2Match.fighter2_stats;
                
              if (stats && stats.wins !== undefined && stats.losses !== undefined && stats.draws !== undefined) {
                fighter2Record = `${stats.wins}-${stats.losses}-${stats.draws}`;
              }
            }
          }
        }
      }
      
      return {
        fighter1: {
          name: fight.fighter1_name,
          winProbability: Math.round(fighter1WinProb * 100),
          record: fighter1Record
        },
        fighter2: {
          name: fight.fighter2_name,
          winProbability: Math.round(fighter2WinProb * 100),
          record: fighter2Record
        },
        explanation: generateExplanation(fight),
        importantStats: getImportantStats(fight, integratedData)
      };
    }));
  }
};

// Fetch most recent upcoming event
export const fetchMostRecentUpcomingEvent = async () => {
  try {
    const client = clearRequestHeaders();
    const response = await client.get(`${API_BASE_URL}/api/predictions`);
    return {
      data: response.data,
      isUpcoming: true
    };
  } catch (error) {
    console.error('Error fetching most recent event:', error);
    throw error;
  }
};

// Helper to extract date from filename
function extractDateFromFilename(filename: string): Date | null {
  const dateMatch = filename.match(/(\d{8})_/);
  if (dateMatch && dateMatch[1]) {
    const year = parseInt(dateMatch[1].substring(0, 4));
    const month = parseInt(dateMatch[1].substring(4, 6)) - 1; // 0-indexed month
    const day = parseInt(dateMatch[1].substring(6, 8));
    return new Date(year, month, day);
  }
  return null;
}

export const fetchFightData = async () => {
  try {
    const response = await fetch(`${API_BASE_URL}/api/predictions`);
    if (!response.ok) {
      throw new Error(`API error: ${response.status}`);
    }
    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error fetching fight data:', error);
    throw error;
  }
}; 